import { differenceInSeconds, format } from "date-fns";

import type { ExtendedClassSessionSummaryType } from "api/api-data-types";
import type {
  ClassSessionSummaryType,
  ClassSessionType,
  ClassSessionUserSummaryType,
} from "components/server-types";

export type AttendanceStatsType = {
  averageAttendance: number;
  highestAttendance: { percentage: number; date: string };
};

/** Compute attendance stats from ClassSessionType + ClassSessionUserSummary (Redux). */
export function computeAttendanceStats(
  sessions: ClassSessionType[],
  studentSummaries: ClassSessionUserSummaryType[],
): AttendanceStatsType {
  const result = {
    averageAttendance: 0,
    highestAttendance: { percentage: 0, date: "" },
  };
  if (sessions.length === 0) return result;

  let totalAttendance = 0;
  let attendanceSessionCount = 0;

  for (const session of sessions) {
    const sessionSummaries = studentSummaries.filter((s) => s.class_session_id === session.id);
    if (sessionSummaries.length > 0) {
      const attending = sessionSummaries.filter((s) => (s.presence_minutes ?? 0) > 0).length;
      const attendancePercentage = (attending / sessionSummaries.length) * 100;
      totalAttendance += attendancePercentage;
      attendanceSessionCount += 1;
      if (attendancePercentage > result.highestAttendance.percentage && session.started_at) {
        result.highestAttendance = {
          percentage: Math.round(attendancePercentage),
          date: format(session.started_at, "MMM d, yyyy"),
        };
      }
    }
  }

  result.averageAttendance =
    attendanceSessionCount > 0 ? Math.round(totalAttendance / attendanceSessionCount) : 0;
  return result;
}

export type StudentTalkTimeStatsType = {
  averageStudentTalkTime: number;
  highestStudentTalkTime: { percentage: number; date: string };
};

/** Compute student talk time stats from ClassSessionType + ClassSessionUserSummary (Redux). */
export function computeStudentTalkTimeStats(
  sessions: ClassSessionType[],
  studentSummaries: ClassSessionUserSummaryType[],
): StudentTalkTimeStatsType {
  const result = {
    averageStudentTalkTime: 0,
    highestStudentTalkTime: { percentage: 0, date: "" },
  };
  if (sessions.length === 0) return result;

  let totalTalkTimePct = 0;
  let talkTimeSessionCount = 0;

  for (const session of sessions) {
    if (session.started_at && session.ended_at) {
      const durationSeconds = differenceInSeconds(session.ended_at, session.started_at);
      if (durationSeconds > 0) {
        const sessionSummaries = studentSummaries.filter((s) => s.class_session_id === session.id);
        const totalStudentTalkSeconds = sessionSummaries.reduce(
          (sum, s) => sum + (s.talk_time_seconds ?? 0),
          0,
        );
        const talkTimePercentage = (totalStudentTalkSeconds / durationSeconds) * 100;

        totalTalkTimePct += talkTimePercentage;
        talkTimeSessionCount += 1;

        if (talkTimePercentage > result.highestStudentTalkTime.percentage) {
          result.highestStudentTalkTime = {
            percentage: Math.round(talkTimePercentage),
            date: format(session.started_at, "MMM d, yyyy"),
          };
        }
      }
    }
  }

  result.averageStudentTalkTime =
    talkTimeSessionCount > 0 ? Math.round(totalTalkTimePct / talkTimeSessionCount) : 0;
  return result;
}

/** Compute ExtendedClassSessionSummaryType from sessions + summaries + user summaries (Redux). */
export function computeExtendedClassSessionSummaries(
  sessions: ClassSessionType[],
  summaries: ClassSessionSummaryType[],
  userSummaries: ClassSessionUserSummaryType[],
  currentUserId: string | null,
): ExtendedClassSessionSummaryType[] {
  const result: ExtendedClassSessionSummaryType[] = [];
  for (const session of sessions) {
    const summary = summaries.find((s) => s.class_session_id === session.id);
    if (!summary || !session.started_at || !session.ended_at) continue; // eslint-disable-line no-continue

    const sessionUserSummaries = userSummaries.filter((us) => us.class_session_id === session.id);
    const students: string[] = [];
    const attendingStudents: string[] = [];
    let instructorTalkTime = 0;
    let studentTalkTime = 0;
    let currentUserPresenceMinutes: number | null = null;

    for (const userSummary of sessionUserSummaries) {
      const presence = userSummary.presence_minutes ?? 0;
      const talkTime = userSummary.talk_time_seconds ?? 0;
      const role = userSummary.role ?? "";

      if (role === "student") {
        students.push(userSummary.user_id);
        // TODO: use same rule everywhere for determining attendance
        if (presence > 0) attendingStudents.push(userSummary.user_id);
        studentTalkTime += talkTime;
      } else if (role === "instructor") {
        instructorTalkTime += talkTime;
      }

      if (currentUserId && userSummary.user_id === currentUserId) {
        currentUserPresenceMinutes = presence;
      }
    }

    result.push({
      ...session,
      ...summary,
      started_at: session.started_at,
      ended_at: session.ended_at,
      students,
      attending_students: attendingStudents,
      instructor_talk_time_seconds: instructorTalkTime,
      student_talk_time_seconds: studentTalkTime,
      student_presence_minutes: currentUserPresenceMinutes ?? null,
    });
  }
  return result;
}
